// Copyright ©️ 2026 eVolve MEP, LLC

import type { TableView } from '@evolvemep/api-ts';
import { alpha, Button, Flex, Indicator, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { GridMenu } from 'components/Mantine/GridMenu';

export type Props = {
  checked: boolean;
  /** Whether the grid has changes that aren't saved to this table view. */
  modified: boolean;
  loading: boolean;
  isDefault: boolean;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  onSave: () => void;
  onChangeDefaultTableView: (tableView: TableView | null) => Promise<void>;
  tableView: TableView;
  canSaveViews: boolean;
};

export const TableViewOption = ({
  checked,
  modified,
  loading,
  isDefault,
  onClick,
  onDelete,
  onRename,
  onSave,
  onChangeDefaultTableView,
  tableView,
  canSaveViews,
}: Props) => (
  <AgGridStyleTooltip
    label="View contains unsaved changes"
    disabled={!modified}
    withArrow
    position="top-start"
    offset={{
      mainAxis: 12,
      crossAxis: -8,
    }}
  >
    <Indicator disabled={!modified} color="yellow.7" position="top-start" processing>
      <Button
        component="div"
        variant={checked ? 'primary' : 'transparent'}
        color="dark"
        onClick={onClick}
        pr={0}
        pl={8}
        size="sm"
        ta="start"
        mih="fit-content"
        fullWidth
        style={{ cursor: 'pointer', flexShrink: 0 }}
        justify="space-between"
        rightSection={
          <GridMenu
            loading={loading}
            {...(checked ? { color: 'inherit' } : {})}
            options={[
              { label: 'Overwrite with current view', value: 'Save', hidden: !canSaveViews },
              { label: isDefault ? 'Remove as default view' : 'Set as default view', value: 'ToggleDefault' },
              { label: 'Rename', value: 'Rename', hidden: !canSaveViews },
              { label: 'Delete', value: 'Delete', hidden: !canSaveViews },
            ]}
            onMenuItemSelected={async (selectedMenuItem) => {
              if (selectedMenuItem === 'Save') {
                onSave();
              }
              if (selectedMenuItem === 'ToggleDefault') {
                const newDefaultTableView = isDefault ? null : tableView;
                await onChangeDefaultTableView(newDefaultTableView);
              }
              if (selectedMenuItem === 'Rename') {
                onRename();
              }
              if (selectedMenuItem === 'Delete') {
                onDelete();
              }
            }}
          />
        }
      >
        <Flex gap="xs" align="center" fw={500} py={4} pr="xs" style={{ whiteSpace: 'normal' }}>
          {checked && <EvolveIcon icon="Check" size="sm" />}
          <Flex direction="column" gap={2}>
            <Text fw="inherit">{tableView.name}</Text>
            {isDefault && (
              <Text
                size="xs"
                c={checked ? alpha('var(--mantine-color-white)', 0.8) : 'gray.6'}
                fw="inherit"
                style={{ whiteSpace: 'nowrap' }}
              >
                (Default)
              </Text>
            )}
          </Flex>
        </Flex>
      </Button>
    </Indicator>
  </AgGridStyleTooltip>
);
